import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";

import ApiServices from "../../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

const ClientCustomers = () => {
  const clientId = sessionStorage.getItem("activeClientId");
  const token = getDecryptedAccessToken();

  const [customers, setCustomers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!clientId) {
      showSnackbar("Client ID not found.", "error");
      return;
    }

    const fetchCustomers = async () => {
      setLoading(true);

      try {
        const response = await ApiServices.get(
          `/api/v1/customers/restaurant/${clientId}/customers/`,
          { Authorization: `Bearer ${token}` }
        );

        const apiData = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        const formattedData = apiData.map((item, index) => ({
          id: item.id || index + 1,
          first_name: item.first_name || "-",
          last_name: item.last_name || "-",
          email: item.email || "-",
          phone: item.phone || "-",
        }));

        setCustomers(formattedData);
      } catch (error) {
        console.error("Error fetching customers:", error);
        showSnackbar("Failed to fetch customers.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [clientId, token]);

  const filteredCustomers = useMemo(() => {
    const search = searchText.toLowerCase().trim();

    if (!search) return customers;

    return customers.filter((customer) => {
      const fullName = `${customer.first_name} ${customer.last_name}`;

      return (
        customer.first_name.toLowerCase().includes(search) ||
        customer.last_name.toLowerCase().includes(search) ||
        fullName.toLowerCase().includes(search) ||
        customer.email.toLowerCase().includes(search) ||
        customer.phone.toLowerCase().includes(search)
      );
    });
  }, [customers, searchText]);

  const columns = [
    {
      field: "first_name",
      headerName: "First Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 240,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 180,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Customers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View customer first name, last name, email and phone number.
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="Search by name, email or phone"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: { xs: "100%", md: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ width: "100%", height: 520 }}>
            <DataGrid
              rows={filteredCustomers}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
              }}
              slots={{
                loadingOverlay: CircularProgress,
              }}
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f9fafb",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientCustomers;